import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
  Switch,
} from 'react-router-dom';
import { connectHits } from 'react-instantsearch-dom';
import axios from 'axios';
import DatePicker from 'react-date-picker';
import { isEqual } from 'lodash';
import Select from 'react-select';
import Form from 'react-bootstrap/Form';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrashAlt,
  faMars,
  faVenus,
} from '@fortawesome/free-solid-svg-icons';
import { getComputedCSSVariable } from '../../../utils/cssVariableHelpers';
import {
  ADMIN_PREFIX,
  ADS_ADMIN_PREFIX,
  ALGOLIA_ALL_LISTINGS_INDEX,
  ALGOLIA_ALL_LISTINGS_INDEX_RECENT,
  BROWSER_API_PREFIX,
  PROFILES_ADMIN_PREFIX,
  PUPPY_DEFAULT_IMAGE,
} from '../../../utils/constants';
import DateHelper from '../../../utils/DateHelper';
import AdminURLSync from '../AdminURLSync';
import { formatCurrency } from '../utils';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import AdsBulkEdit from '../widgets/AdsBulkEdit';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import BreedFilterDisplay from '../widgets/filters/display-components/BreedFilterDisplay';
import StatusFilterDisplay from '../widgets/filters/display-components/StatusFilterDisplay';
import UserFilterDisplay from '../widgets/filters/display-components/UserFilterDisplay';
import AlertFilterDisplay from '../widgets/filters/display-components/AlertFilterDisplay';
import CheckSVG from '../../svgComponents/CheckSVG';
import ExclamationSVG from '../../svgComponents/ExclamationSVG';
import FlagFullSVG from '../../svgComponents/FlagFullSVG';
import FlagEmptySVG from '../../svgComponents/FlagEmptySVG';
import { cloudinaryTransform } from '../../../utils/cloudinaryTransformations';
import { setFallbackImage } from '../../../utils/imageHelper';
import getSearchQueryParamForLink from '../../../utils/getSearchQueryParamForLink';
import routes from '../../../utils/routes';
import AdditionalInfoTextArea from '../seller-dashboard/seller-ads/AdditionalInfoTextArea';
import StatusBadge from './assets/StatusBadge';
import EditSVG from './assets/EditSVG';
import { AdInterface, AlgoliaSimpleFacetOptions, SearchState } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

const Hit: React.FC<AdInterface> = ({
  id,
  admin_breed_display,
  name,
  thumbnailUrl,
  route,
  // created_at,
  // updated_at,
  sex,
  price,
  sale_price,
  seller_name,
  seller_id,
  birth_date,
  availability,
  expires_at,
  sold_at,
  ad_type,
  suspicious_reports,
  is_flagged,
  flag_id,
  label,
  view_count,
  listings_type,
  selected,
  toggleSelected,
  canDelete,
  canSetExpiration,
  trackSelected,
  setTrackedSelected,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const [rowWrapperClass, setRowWrapperClass] = useState('');

  useEffect(() => {
    let newClass = '';
    if (label === 'email') {
      newClass = 'bg-label-email';
    } else if (label === 'full_service') {
      newClass = 'bg-label-full-service';
    }
    if (newClass !== rowWrapperClass) {
      setRowWrapperClass(newClass);
    }
  }, [label, rowWrapperClass]);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
  } = useModal();

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this ad for ${name}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${ADS_ADMIN_PREFIX}/${listings_type}/${id}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      case 'flag':
        setModalData({
          message: `Would you like to flag this ad for ${name}?`,
          ModalForm: () => {
            const [additionalInfo, setAdditionalInfo] = useState('');
            useEffect(() => {
              setModalFormState({ additional_information: additionalInfo });
            }, [additionalInfo]);

            return (
              <div className="form-group">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="additional-info">Additional Information</label>
                <textarea
                  className="form-control"
                  id="additional-info"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e?.target.value)}
                />
              </div>
            );
          },
          onConfirmAsync: (fields) => {
            return new Promise((res, rej) => {
              axios.post(
                `${BROWSER_API_PREFIX}listing/${listings_type}/${id}/flag`,
                fields,
              )
                .then(({ data }) => {
                  res(data.message || 'Listing flagged');
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setModalFormState({});
            updateModal('');
          },
        });
        break;
      case 'un-flag':
        setModalData({
          message: `Would you like to un-flag this ad for ${name}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${BROWSER_API_PREFIX}suspicious-report/${flag_id}`)
                .then(({ data }) => {
                  res(data.message || 'Listing un-flagged');
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setModalFormState({});
            updateModal('');
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${ADMIN_PREFIX}edit/${listings_type}/${id}`}
          aria-label={`Edit listing with name: ${name}.`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete listing with name: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'title',
      value: (
        <a
          className="d-flex align-items-center"
          href={route}
        >
          <img
            className="puppy-hit mr-1"
            alt={`${name}, a ${admin_breed_display} puppy.`}
            src={cloudinaryTransform(
              thumbnailUrl,
              [
                'c_fill',
                'h_26,w_26',
                'f_auto,q_auto',
              ],
            )}
            onError={(e) => {
              setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
            }}
          />
          <span className="puppy-name-wrapper">
            <p className="m-0 text-dark-btn-txt font-weight-bold text-break">{name}</p>
          </span>
        </a>
      ),
    },
    {
      key: 'admin_breed_display',
      value: <p className="text-black m-0">{admin_breed_display}</p>,
    },
    {
      key: 'sex',
      value: sex.toLowerCase() === 'male'
        ? (<FontAwesomeIcon icon={faMars} className="my-auto mx-2 male-icon" />)
        : (<FontAwesomeIcon icon={faVenus} className="my-auto mx-2 female-icon" />),
    },
    {
      key: 'price',
      value: <p className="text-table-light-gray m-0">{formatCurrency(price, true)}</p>,
    },
    {
      key: 'sale_price',
      value: sale_price && <p className="text-table-light-gray m-0">{formatCurrency(sale_price, true)}</p>,
    },
    {
      key: 'seller_profile',
      value: (
        <a
          className="text-table-light-gray m-0"
          href={`${PROFILES_ADMIN_PREFIX}/${seller_id}`}
        >
          {seller_name}
        </a>
      ),
    },
    {
      key: 'birth_date',
      value: DateHelper.getUtcDateTimeFromTimestamp(birth_date)
        .format('MM/DD/YY'),
    },
    // {
    //   key: 'created_at',
    //   value: (
    //     <p className="text-table-light-gray m-0">
    //       {DateHelper.getUtcDateTimeFromTimestamp(created_at).format('MM/DD/YY')}
    //     </p>
    //   ),
    // },
    // {
    //   key: 'updated_at',
    //   value: DateHelper.getUtcDateTimeFromTimestamp(updated_at)
    //     .format('MM/DD/YY'),
    // },
    {
      key: 'availability',
      value: (
        <StatusBadge
          id={id}
          listings_type={listings_type}
          status={availability}
        />
      ),
    },
    // {
    //   key: 'ad_type',
    //   value: <p className="text-table-light-gray m-0 text-capitalize">{ad_type}</p>,
    // },
    {
      key: 'reason',
      value: (
        suspicious_reports.map((suspicious_report) => (
          <div
            key={`${suspicious_report.id}-${suspicious_report.report_type}`}
            className="d-flex align-items-center mb-2"
          >
            <a
              className="text-table-light-gray m-0 mr-2 text-capitalize"
              href={suspicious_report.admin_route}
            >
              {suspicious_report.report_type}
            </a>
            {suspicious_report.acknowledge_user_id
              ? (
                <div className="d-flex justify-content-center align-items-center order border-check-green bg-light-green cleared-icon">
                  <CheckSVG color={getComputedCSSVariable('check-green')} />
                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center border border-exclamation-red cleared-icon">
                  <ExclamationSVG color={getComputedCSSVariable('exclamation-red')} />
                </div>
              )}
          </div>
        ))
      ),
    },
    // {
    //   key: 'sold_at',
    //   value: sold_at
    //     ? (
    //       <p className="text-table-light-gray m-0">
    //         {DateHelper.getUtcDateTimeFromTimestamp(sold_at).format('MM/DD/YY')}
    //       </p>
    //     ) : '',
    // },
    // {
    //   key: 'expires_at',
    //   value: expires_at
    //     ? (
    //       <p className="text-table-light-gray m-0">
    //         {DateHelper.getUtcDateTimeFromTimestamp(expires_at).format('MM/DD/YY')}
    //       </p>
    //     ) : '',
    // },
    // {
    //   key: 'view_count',
    //   value: <p className="text-table-light-gray m-0">{view_count}</p>,
    // },
    {
      key: 'flagged',
      value: is_flagged && flag_id
        ? (
          <a
            href="#"
            className="mx-2"
            aria-label={`Un-flag listing with name: ${name}.`}
            onClick={(e) => {
              e.preventDefault();
              updateModal('un-flag');
            }}
          >
            <FlagFullSVG />
          </a>
        ) : (
          <a
            href="#"
            className="mx-2"
            aria-label={`Flag listing with name: ${name}.`}
            onClick={(e) => {
              e.preventDefault();
              updateModal('flag');
            }}
          >
            <FlagEmptySVG />
          </a>
        ),
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${ADMIN_PREFIX}edit/${listings_type}/${id}`}
          aria-label={`Edit listing with name: ${name}.`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete listing with name: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  const onToggleExtraCallback = useCallback((add: boolean) => {
    if (trackSelected && setTrackedSelected) {
      const newSelected = [...trackSelected.filter((item) => item.id !== id)];
      if (add) {
        newSelected.push({ id, listings_type, flag_id });
      }
      if (!isEqual(newSelected, trackSelected)) {
        setTrackedSelected(newSelected);
      }
    }
  }, [trackSelected, setTrackedSelected]);

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={
            canDelete
              ? values
              : values.filter((val: { key: string }) => val?.key !== 'delete' && val.key !== 'delete-mobile')
          }
          rowWrapperClass={rowWrapperClass}
          id={id}
          onToggleExtraCallback={onToggleExtraCallback}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          title="Confirm Action"
          show={showModal}
          setShow={setShowModal}
          modalFormState={modalFormState}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  canDelete?: boolean,
  canSetExpiration?: boolean,
  selectedListings: {id: number, listings_type: string}[],
  setSelectedListings: (listings: Array<{id: number, listings_type: string}>) => void,
}> = ({
  hits,
  selected,
  toggleSelected,
  canDelete,
  canSetExpiration,
  selectedListings,
  setSelectedListings,
}) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
      canDelete={canDelete}
      canSetExpiration={canSetExpiration}
      trackSelected={selectedListings}
      setTrackedSelected={setSelectedListings}
    />
  );
};

const CustomHits = connectHits(Hits);

interface Props extends RouteComponentProps, AlgoliaSimpleFacetOptions {
  canDelete: boolean,
  canSetExpiration: boolean,
}

const howWasPuppySoldOptions = [
  {
    label: '[admin] Skip',
    value: null,
  },
  {
    label: 'Lancaster Puppies',
    value: 'lancaster_puppies',
  },
  {
    label: 'Other Advertising Site',
    value: 'other_site',
  },
  {
    label: 'Word of Mouth',
    value: 'word_of_mouth',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

const AdminDashboardTableAds: React.FC<Props> = ({
  history,
  location,
  match,
  canDelete,
  canSetExpiration,
  facets,
}): ReactElement => {
  const getTableType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'suspicious'; // Default to 'suspicious'
  };

  const loadInitialSearchState = (tableType) => {
    const facetFilters: string[] = [];
    if (tableType === 'suspicious' || tableType === 'flagged') {
      facetFilters.push('is_suspicious:true');
    }
    if (tableType === 'flagged') {
      facetFilters.push('is_flagged:true');
    }
    return {
      configure: {
        numericFilters: [],
        facetFilters,
      },
      query: '',
    };
  };

  const [tableType, setTableType] = useState(getTableType(location));
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialSearchState(tableType),
  );
  const [breedMax, setBreedMax] = useState<number|null>(null);
  const [selectedListings, setSelectedListings] = useState<
    { id: number, listings_type: string, flag_id?: number }[]
  >([]);
  const [currentSearchResults, setCurrentSearchResults] = useState<Array<any>>([]);

  const toggleAllCallback = useCallback((hits: number[]) => {
    const newSelected: { id: number, listings_type: string, flag_id?: number }[] = [];
    if (hits.length > 0) {
      currentSearchResults.forEach((
        hit: { id: number, listings_type: string, flag_id?: number },
      ) => {
        const { id, listings_type, flag_id } = hit;
        newSelected.push({ id, listings_type, flag_id });
      });
    }

    if (!isEqual(newSelected, selectedListings)) {
      setSelectedListings(newSelected);
    }
  }, [selectedListings, setSelectedListings, currentSearchResults]);

  const updateSearchResults = useCallback((newSearchResults: Array<any>) => {
    if (!isEqual(newSearchResults, currentSearchResults)) {
      setCurrentSearchResults(newSearchResults);
    }
  }, [currentSearchResults]);

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected(toggleAllCallback);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No ads have been selected. Please, select one or more ads to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} ads?`,
            onConfirmAsync: () => {
              const listings = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                };
              });
              return new Promise((res, rej) => {
                axios.post(
                  routes.LISTINGS_ALL_BULK_DELETE,
                  listings,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_sold':
          setModalData({
            message: `Would you like to update the 'Sold Status' of ${bulkSelected.length} ads?`,
            ModalForm: ({ formState }) => {
              useEffect(() => {
                if (formState.availability !== 'sold') {
                  setModalFormState((prevState) => ({ ...prevState, sold_through: '', sold_through_additional_info: '' }));
                }
              }, [formState.availability]);
              return (
                <div>
                  <Select
                    className="mt-1"
                    placeholder="Choose a status..."
                    onChange={(e) => {
                      setModalFormState((prevState) => ({ ...prevState, availability: e?.value }));
                    }}
                    options={[
                      {
                        label: 'Available',
                        value: 'available',
                      },
                      {
                        label: 'Sold',
                        value: 'sold',
                      },
                      {
                        label: 'Pending',
                        value: 'pending',
                      },
                    ]}
                  />
                  {formState?.availability === 'sold' && (
                    <div>
                      <div className="my-1">How was puppy sold?</div>
                      <Select
                        placeholder="Choose an option..."
                        defaultValue={howWasPuppySoldOptions[0]}
                        onChange={(e) => {
                          // eslint-disable-next-line max-len
                          setModalFormState((prevState) => ({ ...prevState, sold_through: e?.value }));
                        }}
                        options={howWasPuppySoldOptions}
                      />
                      <AdditionalInfoTextArea
                        formState={formState}
                        onChange={(e) => {
                          setModalFormState((prevState) => ({
                            ...prevState,
                            sold_through_additional_info: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              const listings = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                };
              });
              return new Promise((res, rej) => {
                axios.patch(
                  routes.LISTINGS_ALL_BULK_UPDATE,
                  {
                    listings,
                    fields,
                  },
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_published':
          setModalData({
            message: `Would you like to update the 'Published Status' of ${bulkSelected.length} ads?`,
            ModalForm: () => {
              return (
                <div>
                  <Select
                    placeholder="Choose a status..."
                    onChange={(e) => {
                      setModalFormState({ status: e?.value });
                    }}
                    options={[
                      {
                        label: 'Published',
                        value: 'published',
                      },
                      {
                        label: 'Unpublished',
                        value: 'unpublished',
                      },
                      {
                        label: 'Draft',
                        value: 'draft',
                      },

                    ]}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              const listings = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                };
              });
              return new Promise((res, rej) => {
                axios.patch(
                  routes.LISTINGS_ALL_BULK_UPDATE,
                  {
                    listings,
                    fields,
                  },
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_expiration':
          setModalData({
            message: `Would you like to update the 'Expiration Date' of ${bulkSelected.length} ads?`,
            ModalForm: () => {
              const [date, setDate] = useState(new Date());
              const [cannotExpire, setCannotExpire] = useState(false);
              useEffect(() => {
                if (date) {
                  setModalFormState({
                    expires_at: moment.utc(date),
                    can_expire: 1,
                  });
                } else if (cannotExpire) {
                  setModalFormState({
                    can_expire: 0,
                  });
                } else {
                  setModalFormState({});
                }
              }, [date, cannotExpire]);
              return (
                <div>
                  <Form>
                    <DatePicker
                      onChange={(val) => setDate(val)}
                      value={date}
                      disabled={cannotExpire}
                    />
                    {cannotExpire && (
                      <Form.Text className="text-warning">Cannot select a date if "Cannot Expire" is checked!</Form.Text>
                    )}
                    <Form.Check
                      type="checkbox"
                      id="mark-no-expire"
                      label='Mark these ads "Cannot Expire"'
                      disabled={!!date}
                      checked={cannotExpire}
                      onChange={() => setCannotExpire(!cannotExpire)}
                    />
                    {!!date && (
                      <Form.Text className="text-warning">Cannot select this option if a date is selected for expiration!</Form.Text>
                    )}
                  </Form>
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              const listings = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                };
              });
              return new Promise((res, rej) => {
                axios.patch(
                  routes.LISTINGS_ALL_BULK_UPDATE,
                  {
                    listings,
                    fields,
                  },
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'flag':
          setModalData({
            message: `Would you like to flag ${bulkSelected.length} ads?`,
            ModalForm: () => {
              // TODO: Could add a debounce here to prevent additional Algolia calls
              const [additionalInfo, setAdditionalInfo] = useState('');
              useEffect(() => {
                setModalFormState({ additional_information: additionalInfo });
              }, [additionalInfo]);

              return (
                <div className="form-group">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="additional-info">Additional Information</label>
                  <textarea
                    className="form-control"
                    id="additional-info"
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e?.target.value)}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              const listingsToFlag = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                  ...fields,
                };
              });
              return new Promise((res, rej) => {
                axios.post(
                  `${BROWSER_API_PREFIX}listing/all/bulk-flag`,
                  listingsToFlag,
                )
                  .then(({ data }) => {
                    res(data.message || 'Listings flagged');
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'un-flag':
          setModalData({
            message: `Would you like to un-flag ${bulkSelected.length} ads?`,
            onConfirmAsync: () => {
              const listingsToUnFlag = selectedListings
                .filter((listing) => listing.flag_id)
                .map((listing) => listing.flag_id);
              return new Promise((res, rej) => {
                axios.delete(`${BROWSER_API_PREFIX}suspicious-report/${listingsToUnFlag.join(',')}/bulk-destroy`)
                  .then(({ data }) => {
                    res(data.message || 'Listing un-flagged');
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'edit':
          if (selectedListings.every(
            (listing) => listing.listings_type === selectedListings[0].listings_type,
          )) {
            setModalData({
              message: `Would you like to bulk edit ${bulkSelected.length} ads? (This will open the bulk edit form.)`,
              onConfirm: () => {
                history.push(
                  `${ADS_ADMIN_PREFIX}/${selectedListings[0].listings_type}/${bulkSelected.join(',')}/bulk-edit`,
                  {
                    adSearchURL: `${location.pathname}/${location.search}`,
                  },
                );
              },
              onClose: () => {
                performBulkAction('');
                setModalFormState({});
              },
              onSuccess: () => setShowModal(false),
            });
          } else {
            setModalData({
              message: 'In order to bulk edit fields, all listings must be of the same type.',
              isSimple: true,
              onConfirm: () => {
                setModalData({
                  message: '',
                  onClose: () => {},
                  isSimple: true,
                });
              },
              onClose: () => {
                setModalData({
                  message: '',
                  onClose: () => {},
                  isSimple: true,
                });
              },
            });
          }
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, selectedListings, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const type = getTableType(location);

    if (type !== tableType) {
      setTableType(type);
    }
  }, [location]);

  useEffect(() => {
    if (tableType === 'suspicious') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['is_suspicious:true'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['is_suspicious:true'],
            },
          });
        }
      }
    } else if (tableType === 'flagged') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['is_suspicious:true', 'is_flagged:true'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['is_suspicious:true', 'is_flagged:true'],
            },
          });
        }
      }
    }
  }, [tableType]);

  const tableHeaderRowValues = [
    {
      key: 'name',
      value: 'Name',
      sort: {
        asc: `${ALGOLIA_ALL_LISTINGS_INDEX}_name_az`,
      },
    },
    {
      key: 'breed',
      value: 'Breed',
      sort: {
        asc: `${ALGOLIA_ALL_LISTINGS_INDEX}_breed_asc`,
      },
    },
    {
      key: 'sex',
      value: 'Sex',
    },
    {
      key: 'price',
      value: 'Asking Price',
      sort: {
        asc: `${ALGOLIA_ALL_LISTINGS_INDEX}_price_asc`,
        desc: `${ALGOLIA_ALL_LISTINGS_INDEX}_price_desc`,
      },
    },
    {
      key: 'sale_price',
      value: 'Sale Price',
      sort: {
        desc: `${ALGOLIA_ALL_LISTINGS_INDEX}_sale_price_desc`,
      },
    },
    {
      key: 'seller',
      value: 'Seller Profile',
    },
    {
      key: 'birth_date',
      value: 'Birth Date',
      sort: {
        desc: `${ALGOLIA_ALL_LISTINGS_INDEX}_birth_date_desc`,
      },
    },
    {
      key: 'availability',
      value: 'Status',
    },
    {
      key: 'reason',
      value: 'Reason',
    },
  ];

  const singleBreedCallback = useCallback((checked: boolean) => {
    if (!checked && breedMax !== 2) {
      setBreedMax(2);
    } else if (breedMax) {
      setBreedMax(null);
    }
  }, [breedMax]);

  const bulkActions = [
    {
      label: 'Set Sold Status',
      value: 'set_sold',
    },
    {
      label: 'Set Published Status',
      value: 'set_published',
    },
    {
      label: 'Bulk Edit Fields',
      value: 'edit',
    },
    {
      label: 'Flag For Review',
      value: 'flag',
    },
    {
      label: 'Un-Flag For Review',
      value: 'un-flag',
    },
  ];

  if (canSetExpiration) {
    bulkActions.push({
      label: 'Set Expiration',
      value: 'set_expiration',
    });
  }

  if (canDelete) {
    bulkActions.push({
      label: 'Delete',
      value: 'delete',
    });
  }

  return (
    <>
      <AdminURLSync
        title="Suspicious Ads"
        ActionButton={undefined}
        numEmptyStartHeaders={canDelete ? 2 : 1}
        numEmptyEndHeaders={canDelete ? 3 : 1}
        numUnhiddenEmptyEndHeaders={1}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        setExtraData={updateSearchResults}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={bulkActions}
        externalQueryParams={[
          {
            key: 'type',
            value: tableType,
          },
          // {
          //   key: 'defaultRefinement',
          //   value: `${ALGOLIA_ALL_LISTINGS_INDEX}_sold_at_desc`,
          // },
        ]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        defaultFacetFilters={[['status:published']]}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={`${ALGOLIA_ALL_LISTINGS_INDEX}_breed_asc`}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
            canDelete={canDelete}
            canSetExpiration={canSetExpiration}
            selectedListings={selectedListings}
            setSelectedListings={setSelectedListings}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'created_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay />,
          },
          {
            type: 'facet',
            attribute: 'admin_breed_parents',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            max: breedMax,
            andSearch: breedMax === 2,
            DisplayComponent: <BreedFilterDisplay />,
            placeholder: '',
            simpleOptions: facets?.admin_breed_parents || [],
          },
          {
            type: 'facet_switch',
            attribute: 'admin_single_breed',
            label: 'Single Breeds Only',
            toggleCallback: singleBreedCallback,
          },
          {
            type: 'facet',
            attribute: 'availability',
            label: 'Status',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <StatusFilterDisplay />,
            placeholder: '',
            simpleOptions: facets?.availability || [],
          },
          {
            type: 'facet',
            attribute: 'suspicious_reports.report_type',
            label: 'Reason',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            isToggleable: false,
            DisplayComponent: <AlertFilterDisplay title="Reason:" />,
            placeholder: '',
            simpleOptions: facets?.report_type || [],
          },
          {
            type: 'facet',
            attribute: 'ad_type',
            label: 'Ad Type',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <StatusFilterDisplay title="Ad Type:" />,
            placeholder: '',
            simpleOptions: facets?.ad_type || [],
          },
          {
            type: 'facet',
            attribute: 'label',
            label: 'Ad Label',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <StatusFilterDisplay title="Ad Label:" />,
            placeholder: '',
            simpleOptions: facets?.label || [],
          },
          {
            type: 'facet',
            attribute: 'status',
            label: 'Publication Status',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <StatusFilterDisplay title="Publication Status:" />,
            placeholder: '',
            simpleOptions: facets?.status || [],
          },
          {
            type: 'facet',
            attribute: 'seller_name',
            label: 'Seller Profile',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Seller Profile:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'external_breeder_id',
            label: 'Seller ID',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Breeder ID:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'username',
            label: 'Account Username',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Account Username:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'listed_by',
            label: 'Listed By',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Listed By:" />,
            placeholder: '',
            simpleOptions: facets?.listed_by || [],
          },
          {
            type: 'facet',
            attribute: 'generation',
            label: 'Generation',
            isSearchable: true,
            isAsync: false,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <BreedFilterDisplay title="Generation:" />,
            placeholder: '',
            simpleOptions: facets?.generation || [],
          },
          {
            type: 'facet',
            attribute: 'sex',
            label: 'Sex',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <BreedFilterDisplay title="Sex:" />,
            placeholder: '',
            simpleOptions: facets?.sex || [],
          },
          {
            type: 'facet',
            attribute: 'colors',
            label: 'Colors',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <BreedFilterDisplay title="Colors:" />,
            placeholder: '',
            simpleOptions: facets?.colors || [],
          },
          {
            type: 'facet',
            attribute: 'size',
            label: 'Size',
            isSearchable: true,
            isAsync: false,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <BreedFilterDisplay title="Size:" />,
            placeholder: '',
            simpleOptions: facets?.size || [],
          },
        ]}
        dateAttributes={[]}
        tabs={[
          {
            title: 'Puppy Ads',
            route: `${ADS_ADMIN_PREFIX}?type=puppy${getSearchQueryParamForLink(location, '&')}`,
            key: 'type=puppy',
            indexName: ALGOLIA_ALL_LISTINGS_INDEX,
            facetFilters: ['listings_type:puppy'],
            numericFilters: [],
          },
          {
            title: 'Stud Dog Ads',
            route: `${ADS_ADMIN_PREFIX}?type=stud${getSearchQueryParamForLink(location, '&')}`,
            key: 'type=stud',
            indexName: ALGOLIA_ALL_LISTINGS_INDEX,
            facetFilters: ['listings_type:stud'],
            numericFilters: [],
          },
          {
            title: 'Suspicious Ads',
            route: `${ADS_ADMIN_PREFIX}?type=suspicious${getSearchQueryParamForLink(location, '&')}`,
            key: 'type=suspicious',
            indexName: ALGOLIA_ALL_LISTINGS_INDEX,
            facetFilters: ['is_suspicious:true'],
            numericFilters: [],
          },
          {
            title: 'Flagged for Review',
            route: `${ADS_ADMIN_PREFIX}?type=flagged${getSearchQueryParamForLink(location, '&')}`,
            key: 'type=flagged',
            indexName: ALGOLIA_ALL_LISTINGS_INDEX,
            facetFilters: ['is_suspicious:true', 'is_flagged:true'],
            numericFilters: [],
          },
          {
            title: 'Recently Sold',
            route: `${ADS_ADMIN_PREFIX}?type=recently-sold&defaultRefinement=${ALGOLIA_ALL_LISTINGS_INDEX}_sold_at_desc${getSearchQueryParamForLink(location, '&')}`,
            key: `type=recently-sold&defaultRefinement=${ALGOLIA_ALL_LISTINGS_INDEX}_sold_at_desc`,
            indexName: ALGOLIA_ALL_LISTINGS_INDEX,
            facetFilters: [],
            numericFilters: ['sold_at > 0'],
          },
          {
            title: 'Recently Updated',
            route: `${ADS_ADMIN_PREFIX}?type=recently-updated${getSearchQueryParamForLink(location, '&')}`,
            key: 'type=recently-updated',
            indexName: ALGOLIA_ALL_LISTINGS_INDEX_RECENT,
            facetFilters: [],
            numericFilters: [],
          },
        ]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminDashboardTableAds;

if (
  document.getElementById('react-admin-dashboard-table-ads-suspicious')
  || document.getElementById('react-admin-dashboard-table-ads-flagged')
) {
  const element = document.getElementById('react-admin-dashboard-table-ads-suspicious')
    || document.getElementById('react-admin-dashboard-table-ads-flagged');

  const canDelete = element?.dataset.candelete
    ? element?.dataset.candelete === '1'
    : false;

  const canSetExpiration = element?.dataset.cansetexpiration
    ? element?.dataset.cansetexpiration === '1'
    : false;

  const facets = element?.dataset.facets && JSON.parse(element?.dataset.facets);

  ReactDOM.render(
    <BrowserRouter>
      <Switch>
        <Route
          path={`${ADS_ADMIN_PREFIX}/:type/:ids/bulk-edit`}
          render={(routeProps) => (
            <AdsBulkEdit
              canSetExpiration={canSetExpiration}
              canDelete={canDelete}
              {...routeProps}
            />
          )}
        />
        <Route
          path={ADS_ADMIN_PREFIX}
          render={(routeProps) => (
            <AdminDashboardTableAds
              canSetExpiration={canSetExpiration}
              canDelete={canDelete}
              facets={facets}
              {...routeProps}
            />
          )}
        />
      </Switch>
    </BrowserRouter>,
    element,
  );
}
